import { isEmpty } from 'lodash';

import {
  isCellWithPipeline,
  replaceCell,
  setCellPipeline,
  restoreInvalidCell,
  isRecordsCell,
} from '@/core/cell';
import { isPipelineChildOf } from '@/core/pipeline';
import { getExplorationCell } from '@/core/exploration';

import {
  AddRelatedColumnOperation,
  Exploration,
  PipelineOperation,
  RelationAggregateOperation,
  Pipeline,
  CellWithPipeline,
  PipelineState,
  Cell,
} from '../../types';
import { getFinalState, PipelineStateContext } from '../../pipeline/state';
import { getCellPipeline, restoreInvalidOperation } from '../../utils';

export const ensureLegalIdentifier = (name: string) => {
  return name.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^([^a-zA-Z])/, '_$1');
};

/**
 * Make names safe-ish as column keys
 */
export const nameToKey = (name: string) => {
  return ensureLegalIdentifier(name).toLowerCase();
};

export const isRecursiveOperation = (
  operation: PipelineOperation,
): operation is AddRelatedColumnOperation | RelationAggregateOperation =>
  operation.operation === 'addRelatedColumn' || operation.operation === 'relationAggregate';

export const adjustFieldOrder = (
  cell: CellWithPipeline,
  updatedPipeline: Pipeline,
  ctx: PipelineStateContext,
) => {
  const existingPipeline = getCellPipeline(cell);
  if (existingPipeline === undefined) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }

  const fieldOrder = cell.viewOptions?.fieldOrder;
  if (isEmpty(fieldOrder)) {
    return cell;
  }

  const previousState = getFinalState(existingPipeline, ctx);
  const nextState = getFinalState(updatedPipeline, ctx);
  const previousKeys = previousState.fields.map((f) => f.key);
  const nextKeys = nextState.fields.map((f) => f.key);

  const previousUnpositionedKeys = previousKeys.filter((key) => !(key in fieldOrder));
  const nextUnpositionedKeys = nextKeys.filter((key) => !(key in fieldOrder));
  const addedKeys = nextUnpositionedKeys.filter((key) => !previousUnpositionedKeys.includes(key));

  const newFieldOrder: Record<string, number> = Object.entries(fieldOrder).reduce(
    (acc, [key, index], keyIndex) => {
      const previousPrecedingCount = index - keyIndex;
      const previousPrecedingKeys = previousUnpositionedKeys.slice(0, previousPrecedingCount);
      const nextPrecedingCount = nextUnpositionedKeys.findIndex(
        (k) => !previousPrecedingKeys.includes(k) && !addedKeys.includes(k),
      );

      // Let new fields at the positioned index come after the positioned field
      const nextPrecedingCountAdjusted =
        nextUnpositionedKeys.findLastIndex(
          (k, i) => !addedKeys.includes(k) && i < nextPrecedingCount,
        ) + 1;

      const precedingCountAfter = nextPrecedingCountAdjusted + keyIndex;
      return {
        ...acc,
        [key]: precedingCountAfter,
      };
    },
    {},
  );

  return {
    ...cell,
    viewOptions: {
      ...cell.viewOptions,
      fieldOrder: newFieldOrder,
    },
  };
};

export const adjustCellSort = <T extends Cell>(
  cell: T,
  previousState: PipelineState,
  nextState: PipelineState,
): T => {
  if (!isRecordsCell(cell)) {
    return cell;
  }

  const groupsChanged =
    previousState.grouping.length !== nextState.grouping.length ||
    previousState.grouping.some((g, i) => g.key !== nextState.grouping[i].key);

  if (groupsChanged && nextState.grouping.length > 0) {
    return {
      ...cell,
      sort: nextState.grouping.map((g) => ({ key: g.key, direction: 'ASC' as const })),
    };
  }

  return cell;
};

export const updateCellPipeline = (
  exploration: Exploration,
  cellId: string,
  pipeline: Pipeline,
  ctx: PipelineStateContext,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const restoredCell = restoreInvalidCell(cell);
  if (!isCellWithPipeline(restoredCell)) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }

  const previousState = getFinalState(restoredCell.pipeline, ctx);
  const nextState = getFinalState(pipeline, ctx);

  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: replaceCell(
        exploration.view.cells,
        setCellPipeline(
          adjustCellSort(adjustFieldOrder(restoredCell, pipeline, ctx), previousState, nextState),
          pipeline,
        ),
        restoredCell.id,
      ),
    },
  };
};

export const replaceOperations = (
  exploration: Exploration,
  cellId: string,
  operations: PipelineOperation[],
  ctx: PipelineStateContext,
) => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }
  return updateCellPipeline(exploration, cell.id, { ...pipeline, operations }, ctx);
};

export const getDescendantPipelines = (
  parentPipeline: Pipeline,
  exploration: Exploration,
): Pipeline[] => {
  return exploration.view.cells.flatMap((cell) => {
    const pipeline = getCellPipeline(cell);
    const isChild = pipeline !== undefined && isPipelineChildOf(pipeline, parentPipeline);
    if (isChild) {
      return [pipeline, ...getDescendantPipelines(pipeline, exploration)];
    }
    return [];
  });
};

const applyOperationToPipeline = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operation: PipelineOperation,
  ctx: PipelineStateContext,
  replace?: boolean,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to update an operation on a cell without a pipeline');
  }

  const operations = pipeline.operations;
  const before = operations.slice(0, operationIndex);
  const after = operations.slice(operationIndex + (replace === true ? 1 : 0));
  return replaceOperations(
    exploration,
    cell.id,
    [...before, restoreInvalidOperation(operation), ...after],
    ctx,
  );
};

export const addOperation = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operation: PipelineOperation,
  ctx: PipelineStateContext,
): Exploration => {
  return applyOperationToPipeline(exploration, cellId, operationIndex, operation, ctx);
};

export const addOperations = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operations: PipelineOperation[],
  ctx: PipelineStateContext,
): Exploration => {
  return operations.reduce(
    (exploration, operation, idx) =>
      addOperation(exploration, cellId, operationIndex + idx, operation, ctx),
    exploration,
  );
};

export const updateOperation = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  operation: PipelineOperation,
  ctx: PipelineStateContext,
): Exploration => {
  return applyOperationToPipeline(exploration, cellId, operationIndex, operation, ctx, true);
};

export const removeOperation = (
  exploration: Exploration,
  cellId: string,
  operationIndex: number,
  ctx: PipelineStateContext,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to remove an operation from a cell without a pipeline');
  }

  return replaceOperations(
    exploration,
    cellId,
    [
      ...pipeline.operations.slice(0, operationIndex),
      ...pipeline.operations.slice(operationIndex + 1),
    ],
    ctx,
  );
};

// TODO: move to core
export const moveOperation = (
  exploration: Exploration,
  cellId: string,
  fromIndex: number,
  toIndex: number,
  ctx: PipelineStateContext,
): Exploration => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined) {
    throw new Error('Cell not found');
  }
  const pipeline = getCellPipeline(cell);
  if (pipeline === undefined) {
    throw new Error('Attempting to remove an operation from a cell without a pipeline');
  }

  const tempPipeline = [
    ...pipeline.operations.slice(0, fromIndex),
    ...pipeline.operations.slice(fromIndex + 1),
  ];

  return replaceOperations(
    exploration,
    cell.id,
    [
      ...tempPipeline.slice(0, toIndex),
      pipeline.operations[fromIndex],
      ...tempPipeline.slice(toIndex),
    ],
    ctx,
  );
};
